// src/pages/EventPublic.jsx

import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import moment from "moment";
import "moment/locale/id";
import {
  Calendar,
  Clock,
  MapPin,
  Ticket,
  ArrowLeft,
  ShieldCheck,
  ChevronRight,
  X,
  SearchX,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import api from "../services/api";
import { cleanQuillHtml } from "../utils/cleanQuillHtml";
import PublicSplashScreen from "../components/PublicSplashScreen";
import ErrorState from "../components/ErrorState";
import BookingModal from "./booking/BookingModal";
import "./event-public.scss";

export default function EventPublic() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);
  const [showMobileSheet, setShowMobileSheet] = useState(false);

  useEffect(() => {
    async function fetchEvent() {
      try {
        const res = await api.get(`/api/events/${id}`);
        setEvent(res.data);
      } catch (err) {
        setError(err.response?.data?.message || "Event tidak ditemukan");
      } finally {
        setLoading(false);
      }
    }
    fetchEvent();
  }, [id]);

  // Lock body scroll when sheet is open
  useEffect(() => {
    document.body.style.overflow = showMobileSheet ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [showMobileSheet]);

  if (loading) return <PublicSplashScreen />;

  if (error || !event)
    return (
      <ErrorState
        icon={SearchX}
        title="Event Tidak Ditemukan"
        message={error || "Event yang kamu cari tidak tersedia atau sudah tidak aktif."}
        action={{ label: "Lihat Semua Event", href: "/events" }}
        secondaryAction={{ label: "← Kembali ke Beranda", href: "/" }}
      />
    );

  const {
    title,
    date,
    timeStart,
    timeEnd,
    location,
    ticketTiers = [],
    headerImage,
    description,
    whatsappPic,
  } = event;

  /* ── CUT-OFF LOGIC ───────────────────────────── */
  let isCutOff = false;
  if (date && timeStart) {
    const eventDateTime = moment(
      `${moment(date).format("YYYY-MM-DD")} ${timeStart}`,
      "YYYY-MM-DD HH:mm"
    );
    const now = moment();
    const sameDay = now.isSame(eventDateTime, "day");
    if (sameDay) {
      const cutOffTime =
        eventDateTime.hour() >= 15
          ? moment(eventDateTime).hour(15).minute(0).second(0)
          : moment(eventDateTime).subtract(3, "hours");
      isCutOff = now.isAfter(cutOffTime);
    }
    if (now.isAfter(eventDateTime)) isCutOff = true;
  }

  const waLink = whatsappPic
    ? `https://wa.me/${whatsappPic}?text=${encodeURIComponent(
        `Halo, saya ingin bertanya mengenai event "${title}".`
      )}`
    : null;

  const formattedDate = date
    ? moment(date).locale("id").format("dddd, D MMMM YYYY")
    : "Menyusul";

  const formattedTime = timeStart
    ? `${timeStart}${timeEnd ? ` – ${timeEnd}` : ""} WIB`
    : "Menyusul";

  /* ── MOBILE BAR DERIVED VALUES ───────────────── */
  const availableTiers = ticketTiers.filter(
    (t) => (t.quota > 0 ? t.quota - (t.sold || 0) : 1) > 0
  );
  const allSoldOut = ticketTiers.length > 0 && availableTiers.length === 0;
  const lowestTier = [...availableTiers].sort((a, b) => a.price - b.price)[0];

  const mobileBarDisabled = isCutOff || allSoldOut;
  const mobileBarCta = isCutOff
    ? "Tiket Ditutup"
    : allSoldOut
    ? "Tiket Habis"
    : "Dapatkan Tiket";
  const mobileBarPrice =
    !lowestTier
      ? null
      : lowestTier.price === 0
      ? "Gratis"
      : `Mulai Rp ${lowestTier.price.toLocaleString("id-ID")}`;

  /* ── MAPS HREF (safety net for existing events with plain-text mapUrl) ─── */
  const mapsHref = location?.mapUrl?.startsWith("http")
    ? location.mapUrl
    : location?.mapUrl
    ? `https://maps.google.com/maps?q=${encodeURIComponent(location.mapUrl)}`
    : null;

  /* ── BOOKING HANDLER ─────────────────────────── */
  async function handleBookingConfirm(formData) {
    const payload = {
      eventId: event._id,
      tierId: formData.tierId,
      quantity: formData.quantity || 1,
      name: formData.name.trim(),
      email: formData.email.trim().toLowerCase(),
      phone: formData.phone?.trim() || "",
      paymentMethod: "QRIS",
    };
    try {
      const res = await api.post("/api/bookings", payload);
      const booking = res.data?.data?.booking;
      if (!booking?._id) {
        alert("Terjadi kesalahan sistem.");
        return;
      }
      setSelectedTier(null);
      navigate(`/booking/${booking._id}/pay`);
    } catch (err) {
      console.error("BOOKING ERROR:", err);
      alert(err.response?.data?.message || "Gagal membuat booking");
    }
  }

  function handleSelectTier(tier) {
    setShowMobileSheet(false);
    setSelectedTier(tier);
  }

  return (
    <>
      <div className="ep">

        {/* ══════════════════ HERO ══════════════════ */}
        <section className="ep__hero">
          <img
            className="ep__hero-img"
            src={headerImage?.url || "/placeholder-event.jpg"}
            alt={title}
          />
          <div className="ep__hero-overlay" />

          <div className="ep__hero-content">
            <Link to="/events" className="ep__back">
              <ArrowLeft size={14} />
              Semua Event
            </Link>
            <h1 className="ep__hero-title">{title}</h1>
            <div className="ep__hero-chips">
              <span className="ep__chip">
                <Calendar size={14} />
                {formattedDate}
              </span>
              <span className="ep__chip">
                <Clock size={14} />
                {formattedTime}
              </span>
              {location?.name && (
                <span className="ep__chip">
                  <MapPin size={14} />
                  {location.name}
                </span>
              )}
            </div>
          </div>
        </section>

        {/* ══════════════════ BODY ══════════════════ */}
        <div className="ep__body">
          <div className="ep__grid">

            {/* ─── MAIN ─────────────────────────── */}
            <main className="ep__main">

              <section className="ep__section">
                <h2 className="ep__section-title">Tentang Acara</h2>
                <div
                  className="ep__description"
                  dangerouslySetInnerHTML={{
                    __html: cleanQuillHtml(
                      description || "<p>Detail acara akan diumumkan segera.</p>"
                    ),
                  }}
                />
              </section>

              {location?.embedUrl && (
                <section className="ep__section">
                  <h2 className="ep__section-title">
                    <MapPin size={18} />
                    Lokasi
                  </h2>
                  {location.address && (
                    <p className="ep__address">{location.address}</p>
                  )}
                  <div className="ep__map">
                    <iframe
                      src={location.embedUrl}
                      title="Lokasi Event"
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                    {mapsHref && (
                      <a
                        href={mapsHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ep__map-overlay"
                        aria-label="Buka di Google Maps"
                      />
                    )}
                  </div>
                  {mapsHref && (
                    <a
                      href={mapsHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ep__maps-link"
                    >
                      Buka di Google Maps
                      <ChevronRight size={14} />
                    </a>
                  )}
                </section>
              )}
            </main>

            {/* ─── SIDEBAR (desktop only) ────────── */}
            <aside className="ep__sidebar">
              <div className="ep__card">
                {isCutOff && (
                  <div className="ep__cutoff">
                    <span>Pendaftaran tiket telah ditutup</span>
                  </div>
                )}
                <div className="ep__card-head">
                  <h3>Pilih Tiket</h3>
                </div>
                <div className="ep__tiers">
                  {ticketTiers.map((tier) => (
                    <TierItem
                      key={tier._id}
                      tier={tier}
                      isCutOff={isCutOff}
                      onSelect={setSelectedTier}
                    />
                  ))}
                </div>
                {waLink && (
                  <a href={waLink} target="_blank" rel="noopener noreferrer" className="ep__wa">
                    <FaWhatsapp size={17} />
                    Hubungi PIC via WhatsApp
                  </a>
                )}
                <div className="ep__secure">
                  <ShieldCheck size={13} />
                  Pembayaran aman via QRIS
                </div>
              </div>
            </aside>

          </div>
        </div>

        {/* ══════════════════ MOBILE STICKY BAR ══════════════════ */}
        <div className={`ep__mobile-bar${mobileBarDisabled ? " ep__mobile-bar--disabled" : ""}`}>
          <div className="ep__mobile-bar-info">
            <p className="ep__mobile-bar-label">Harga tiket</p>
            <p className="ep__mobile-bar-price">
              {mobileBarPrice || "Lihat tiket"}
            </p>
          </div>
          <button
            className="ep__mobile-bar-btn"
            disabled={mobileBarDisabled}
            onClick={() => setShowMobileSheet(true)}
          >
            {!mobileBarDisabled && <Ticket size={16} />}
            {mobileBarCta}
          </button>
        </div>

        {/* ══════════════════ MOBILE BOTTOM SHEET ══════════════════ */}
        {showMobileSheet && (
          <>
            <div
              className="ep__sheet-backdrop"
              onClick={() => setShowMobileSheet(false)}
            />
            <div className="ep__sheet">
              <div className="ep__sheet-handle" />

              <div className="ep__sheet-head">
                <h3>Pilih Tiket</h3>
                <button
                  className="ep__sheet-close"
                  onClick={() => setShowMobileSheet(false)}
                  aria-label="Tutup"
                >
                  <X size={18} />
                </button>
              </div>

              {isCutOff && (
                <div className="ep__cutoff ep__cutoff--sheet">
                  <span>Pendaftaran tiket telah ditutup</span>
                </div>
              )}

              <div className="ep__sheet-tiers">
                {ticketTiers.map((tier) => (
                  <TierItem
                    key={tier._id}
                    tier={tier}
                    isCutOff={isCutOff}
                    onSelect={handleSelectTier}
                  />
                ))}
              </div>

              {waLink && (
                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ep__wa ep__wa--sheet"
                >
                  <FaWhatsapp size={17} />
                  Hubungi PIC via WhatsApp
                </a>
              )}

              <div className="ep__secure ep__secure--sheet">
                <ShieldCheck size={13} />
                Pembayaran aman via QRIS
              </div>
            </div>
          </>
        )}

      </div>

      {selectedTier && (
        <BookingModal
          event={event}
          tier={selectedTier}
          onClose={() => setSelectedTier(null)}
          onConfirm={handleBookingConfirm}
        />
      )}
    </>
  );
}

/* ── Shared Tier Item ──────────────────────────── */
function TierItem({ tier, isCutOff, onSelect }) {
  const remaining =
    tier.quota > 0 ? Math.max(tier.quota - (tier.sold || 0), 0) : null;
  const isSoldOut = remaining === 0;
  const isDisabled = isSoldOut || isCutOff;

  return (
    <div className={`ep__tier${isDisabled ? " ep__tier--disabled" : ""}`}>
      <div className="ep__tier-top">
        <div>
          <p className="ep__tier-name">{tier.name}</p>
          {tier.description && (
            <p className="ep__tier-desc">{tier.description}</p>
          )}
        </div>
        <p className="ep__tier-price">
          {tier.price === 0
            ? "Gratis"
            : `Rp ${tier.price.toLocaleString("id-ID")}`}
        </p>
      </div>

      <button
        className="ep__tier-btn"
        disabled={isDisabled}
        onClick={() => onSelect(tier)}
      >
        {isCutOff ? (
          "Tiket Ditutup"
        ) : isSoldOut ? (
          "Tiket Habis"
        ) : (
          <>
            <Ticket size={15} />
            Dapatkan Tiket
          </>
        )}
      </button>
    </div>
  );
}

